import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./RoomCard1.css";

/* ═══════════════════════════════════════════════════════════════
STICKY BOTTOM BAR — tablet & mobile only (≤1199px)
═══════════════════════════════════════════════════════════════ */
const StickyBottomBar = ({ summary, subtotal, formatSummaryDate, bookingDate, onNextStep, onDismiss, totalGuests, glass3DCost }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <div
        className={`sticky-bar-backdrop ${visible ? "visible" : ""}`}
        onClick={onDismiss}
      />
      <div className={`sticky-bottom-bar ${visible ? "visible" : ""}`}>
        <div className="sticky-bar-handle-row">
          <div className="sticky-bar-handle" />
          <button className="sticky-bar-dismiss" onClick={onDismiss} aria-label="Close">✕</button>
        </div>

        <div className="sticky-bar-content">
          <div className="sticky-bar-room-row">
            <div className="sticky-bar-room-icon">🎭</div>
            <div className="sticky-bar-room-info">
              <div className="sticky-bar-room-name">{summary.theater}</div>
              <div className="sticky-bar-meta">
                <span className="sticky-bar-slot-chip">
                  <span className="sticky-bar-slot-dot" />
                  {summary.slot}
                </span>
                {bookingDate && (
                  <>
                    <span className="sticky-bar-meta-dot" />
                    <span>📅 {formatSummaryDate(bookingDate)}</span>
                  </>
                )}
                <span className="sticky-bar-meta-dot" />
                <span style={{ color: '#312AB4', fontWeight: '600' }}>🥽 3D Glasses ({totalGuests}): ₹{glass3DCost}</span>
              </div>
            </div>
            <div className="sticky-bar-price-badge">
              <span className="sticky-bar-price-label">Total</span>
              <span className="sticky-bar-price-val">₹{subtotal.toLocaleString()}</span>
            </div>
          </div>
          <div className="sticky-bar-divider" />
          <div className="sticky-bar-btns">
            <button className="sticky-bar-btn-secondary" onClick={onNextStep}>
              📋 Add Details
            </button>
            <button className="sticky-bar-btn-primary" onClick={onNextStep}>
              🎂 Cakes &amp; Add-ons
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
MAIN 3D GLASS ROOM CARDS COMPONENT
═══════════════════════════════════════════════════════════════ */
const RoomCards1 = () => {
  const navigate = useNavigate();
  
  const [data, setData] = useState({ location: "", date: "" });
  const [editMode, setEditMode] = useState(false);
  
  const [maxLimit, setMaxLimit] = useState(10);
  const [showPopup, setShowPopup] = useState(false);
  const [tempSlot, setTempSlot] = useState("");
  const [tempPeople, setTempPeople] = useState({ adults: 4, kids: 0 });

  const [summary, setSummary] = useState({
    theater: "",
    theaterId: "",
    price: 0,
    slot: "",
    occasion: "",
    basePeople: 4,
    extraAdultPrice: 400,
    kidPrice: 200,
    maxPeople: 10,
    cakes: [],
    extraDecors: [],
    gifts: [],
    services: [],
  });
  
  const [people, setPeople] = useState({ adults: 4, kids: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSummaryOpenMobile, setIsSummaryOpenMobile] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not Selected";
    const [y, m, d] = dateStr.split("-");
    const dateObj = new Date(y, m - 1, d);
    return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatSummaryDate = (dateStr) => {
    if (!dateStr) return "Not Selected";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("bookingData") || "null");
    if (savedUser) {
      setData({ location: savedUser.location || "", date: savedUser.date || "" });
    }
    setTimeout(() => setIsLoaded(true), 150);
  }, []);

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const singleTheaterRoom = {
    id: "premium-3d-cinema-hall",
    title: "HexaPlay Ultra Premium 3D Private Theater",
    location: data.location || "Select Location Above",
    images: [
      "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1595769816263-9b910be24d5f?auto=format&fit=crop&w=800&q=80"
    ],
    videoUrl: "https://www.w3schools.com/html/mov_bbb.mp4",
    rating: "4.9",
    price: 2500,
    basePeople: 4,
    extraAdultPrice: 400,
    kidPrice: 200,
    maxPeople: maxLimit,
    timeSlots: ["10:00 AM", "01:30 PM", "04:45 PM", "08:00 PM", "11:15 PM"],
    slotsAvailable: 5,
    hasFood: true,
    hasFreeCancellation: true,
    hasDecor: true,
    has3DGlass: true,
    discountSlots: [1, 3],
  };

  const handleSlotClick = (room, slot) => {
    if (!data.location || !data.date) {
      alert("❌ Selection Mandatory: Please select BOTH Location and Date at the top of the page before choosing a time slot!");
      return;
    }

    setTempSlot(slot);
    setTempPeople({ adults: summary.basePeople || 4, kids: 0 });
    setShowPopup(true);
  };

  const handleConfirmGuests = () => {
    const totalCount = tempPeople.adults + tempPeople.kids;
    if (totalCount > maxLimit) {
      alert(`❌ Limit Exceeded: The maximum limit set by admin is ${maxLimit} people. You have selected ${totalCount} people.`);
      return;
    }
    if (tempPeople.adults < 1) {
      alert("❌ Invalid Guest Count: There must be at least 1 adult to make a booking.");
      return;
    }

    const updatedSummary = {
      theater: singleTheaterRoom.title,
      theaterId: singleTheaterRoom.id,
      price: singleTheaterRoom.price,
      slot: tempSlot,
      basePeople: singleTheaterRoom.basePeople,
      extraAdultPrice: singleTheaterRoom.extraAdultPrice,
      kidPrice: singleTheaterRoom.kidPrice,
      maxPeople: maxLimit,
      occasion: "",
      cakes: [],
      extraDecors: [],
      gifts: [],
      services: [],
    };

    setSummary(updatedSummary);
    setPeople({ adults: tempPeople.adults, kids: tempPeople.kids });
    setShowPopup(false);

    if (window.innerWidth <= 1199) {
      setIsSummaryOpenMobile(true);
    }
  };

  // Calculations - 3D glass cost (Rs.300 per person)
  const totalGuests = people.adults + people.kids;
  const glass3DCost = totalGuests * 300;
  
  const extraAdults = Math.max(0, people.adults - summary.basePeople);
  const adultExtra = extraAdults * summary.extraAdultPrice;
  const kidExtra = people.kids * summary.kidPrice;
  
  // Subtotal WITHOUT decoration (decoration added in OccasionSelection)
  const subtotal = summary.price + adultExtra + kidExtra + glass3DCost;

  const [activeImageIndex, setActiveImageIndex] = useState({});
  const next = (id, imagesCount) => {
    setActiveImageIndex((prev) => ({ ...prev, [id]: ((prev[id] || 0) + 1) % imagesCount }));
  };
  const prev = (id, imagesCount) => {
    setActiveImageIndex((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) === 0 ? imagesCount - 1 : (prev[id] || 0) - 1,
    }));
  };

  const handleNextStep = () => {
    localStorage.setItem(
      "currentBookingSummary",
      JSON.stringify({ 
        ...summary, 
        date: data.date, 
        location: data.location,
        maxPeople: maxLimit,
        peopleState: people,
        is3DRoom: true, // FLAG: True for 3D room booking
        glass3DCost: glass3DCost, // Pre-calculated 3D glass cost
        totalGuests: totalGuests
      })
    );
    navigate("/OccasionSelection");
  };

  const activeImg = activeImageIndex[singleTheaterRoom.id] || 0;

  return (
    <div className={`room-page ${isLoaded ? "loaded" : ""}`}>
      <div className="bg-gradient-mesh"></div>

      {/* ── DESKTOP HEADER BAR FOR MANDATORY DATE & LOCATION ── */}
      <div className="d-only booking-info">
        <div className="info-left">
          <div className="info-box">
            <label>Select Location *</label>
            <select name="location" value={data.location} onChange={handleChange}>
              <option value="">-- Choose Location (Mandatory) --</option>
              <option value="Chennai">Chennai</option>
              <option value="Thane">Thane</option>
              <option value="Pondicherry">Pondicherry</option>
            </select>
          </div>
          <div className="info-box">
            <label>Select Booking Date *</label>
            <input type="date" name="date" min={today} value={data.date} onChange={handleChange} />
          </div>
        </div>
        <div>
          <span style={{ fontSize: "14px", fontWeight: "700", color: "#312AB4", background: "#f0eeff", padding: "8px 16px", borderRadius: "10px", border: "1px solid #c7c3f7" }}>
            🎬 Elite 3D Private Theatre Booking Panel
          </span>
        </div>
      </div>

      {/* ── MOBILE HEADER ── */}
      <div className="m-only block m-header-container">
        <div className="m-back-row" onClick={() => navigate(-1)}>
          <span className="m-back-arrow">←</span> Back
        </div>
        <div className="m-info-pill">
          <div className="m-info-texts">
            <span className="m-loc-title" style={{ color: data.location ? "#111827" : "#dc2626" }}>
              📍 {data.location || "Select Location (Mandatory)"}
            </span>
            <span className="m-date-title" style={{ color: data.date ? "#6b7280" : "#dc2626", fontWeight: data.date ? "400" : "600" }}>
              📅 {data.date ? formatDate(data.date) : "Select Date (Mandatory)"}
            </span>
          </div>
          <button className="m-update-btn-header" onClick={() => setEditMode(!editMode)}>
            {editMode ? "Close" : "Update"}
          </button>
        </div>
        {editMode && (
          <div className="m-edit-mode-panel" style={{ flexDirection: "column", background: "#f9fafb", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", gap: "10px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151" }}>Location:</label>
            <select name="location" value={data.location} onChange={handleChange} style={{ width: "100%" }}>
              <option value="">Select Location</option>
              <option value="Chennai">Chennai</option>
              <option value="Thane">Thane</option>
              <option value="Pondicherry">Pondicherry</option>
            </select>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151" }}>Date:</label>
            <input type="date" name="date" min={today} value={data.date} onChange={handleChange} style={{ width: "100%" }} />
          </div>
        )}
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div className="booking-layout">
        <div className="room-list-wrapper">
          
          {(!data.location || !data.date) && (
            <div className="mandatory-warning-bar">
              ⚠️ <b>Booking Status:</b> Please select a location and a date. These fields are mandatory before choosing an available time slot.
            </div>
          )}

          {/* ROOM CARD DISPLAY */}
          <div className={`room-card-horizontal ${summary.slot ? "card-selected" : ""}`}>
            {/* ── IMAGE ── */}
            <div className={`card-image-box ${summary.slot ? "card-image-selected" : ""}`}>
              <div className="image-gradient-overlay d-only"></div>
              <img src={singleTheaterRoom.images[activeImg]} alt={singleTheaterRoom.title} className="card-img" />

              <div className="card-badge-premium d-only"><span>⭐ Premium 3D</span></div>
              <div className="card-badge-3d d-only">
                <span className="badge-3d-glass-icon">🥽</span>
                <span>3D Glasses Provided</span>
              </div>
              <div className="card-rating d-only">
                <span>★ {singleTheaterRoom.rating}</span>
              </div>
              
              <button className="card-nav left-nav" onClick={(e) => { e.stopPropagation(); prev(singleTheaterRoom.id, singleTheaterRoom.images.length); }}>‹</button>
              <button className="card-nav right-nav" onClick={(e) => { e.stopPropagation(); next(singleTheaterRoom.id, singleTheaterRoom.images.length); }}>›</button>
              
              <div className="image-indicators d-only">
                {singleTheaterRoom.images.map((_, i) => (
                  <span key={i} className={`indicator ${i === activeImg ? "active" : ""}`}></span>
                ))}
              </div>

              <div className="m-only m-rating-pill">
                <span className="m-star">★</span> {singleTheaterRoom.rating}
              </div>
              <div className="m-only m-3d-badge">
                <span className="m-3d-icon">🥽</span>
                <span>3D Cinema</span>
              </div>
              <div className="m-only m-video-btn">
                <span className="m-play-icon">▶</span> 3D Dolby sound
              </div>
            </div>

            {/* ── DETAILS ── */}
            <div className="card-details-box">
              <div className="card-header-flex">
                <div className="header-titles">
                  <div className="header-title-row">
                    <h2>{singleTheaterRoom.title}</h2>
                    <span className="inline-3d-tag">
                      <span className="inline-3d-icon">🥽</span> Premium 3D Glass Room
                    </span>
                  </div>

                  <div className="m-only m-location-row">
                    <span className="m-pin-icon">📍</span>
                    <span className="m-loc-name">{data.location || "Location Not Selected"}</span>
                    <span className="m-slots-badge">{singleTheaterRoom.slotsAvailable} Slots Left</span>
                  </div>
                </div>
                <div className="header-meta d-only">
                  <div className="meta-pill"><span>👥 Max Limit: {maxLimit} Pax</span></div>
                  <div className="meta-pill highlight"><span>🥽 3D Glass ₹300/person</span></div>
                </div>
              </div>

              <div className="m-only m-features-text-row">
                <span className="m-feat-text" style={{ display: "inline-block", padding: "10px 14px", backgroundColor: "#2018b6", color: "#fff", borderRadius: "8px", fontWeight: "600", fontSize: "11px", textAlign: "center" }}>
                  🎁 Special Discount of ₹200 Applied via Coupons in Next Step!
                </span>
                <span className="m-feat-text m-feat-3d-glass">🥽 3D Glasses: ₹300 / Person</span>
                <span className="m-feat-text">☕ Snacks Available</span>
                <span className="m-feat-text">🏷️ Free Rescheduling</span>
              </div>

              {/* ── SLOTS ── */}
              <div className="slots-section">
                <h4>⏰ Available Time Slots</h4>
                <div className="card-slots-grid">
                  {singleTheaterRoom.timeSlots.map((slot, i) => {
                    const isDiscount = singleTheaterRoom.discountSlots.includes(i);
                    const isSlotActive = summary.slot === slot;
                    return (
                      <button
                        key={i}
                        className={`slot-btn ${isSlotActive ? "active" : ""}`}
                        onClick={() => handleSlotClick(singleTheaterRoom, slot)}
                      >
                        <span className="slot-time">{slot}</span>
                        {isDiscount && (
                          <span className="slot-discount-tag d-only">Special Discount</span>
                        )}
                        {isSlotActive && <span className="slot-check">✓ Selected</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── PRICE BOX ── */}
            <div className="card-price-box">
              <div className="price-tag-container">
                <div className="price-tag">
                  <span className="currency">₹</span>
                  <span className="amount">{singleTheaterRoom.price.toLocaleString()}</span>
                </div>
                <span className="price-duration">Theater base price</span>
              </div>
              <div className="price-features">
                <div className="feature-item"><span>✓ Up to {singleTheaterRoom.basePeople} people</span></div>
                <div className="feature-item"><span>✓ +₹{singleTheaterRoom.extraAdultPrice} / extra adult</span></div>
                <div className="feature-item"><span>✓ +₹{singleTheaterRoom.kidPrice} / kid guest</span></div>
                <div className="feature-item" style={{ color: "#fbbf24", fontWeight: "700" }}><span>🥽 ₹300 3D glass / person</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── DESKTOP SIDE SUMMARY ── */}
        {summary.slot && (
          <div className="booking-summary-wrapper d-only">
            <div className="booking-summary">
              <div className="summary-header">
                <div className="summary-icon-box">🥽</div>
                <div>
                  <h3>Booking Summary</h3>
                  <p className="summary-subtitle">Private 3D Room Experience</p>
                </div>
              </div>
              <div className="summary-content">
                <div className="summary-card">
                  <div className="summary-row">
                    <span className="row-label">🎬 Theater</span>
                    <b>{summary.theater}</b>
                  </div>
                  <div className="summary-row">
                    <span className="row-label">⏰ Time Slot</span>
                    <b style={{ color: "#312AB4" }}>{summary.slot}</b>
                  </div>
                  <div className="summary-row">
                    <span className="row-label">📅 Date</span>
                    <b>{formatSummaryDate(data.date)}</b>
                  </div>
                  <div className="summary-row">
                    <span className="row-label">📍 Location</span>
                    <b>{data.location}</b>
                  </div>
                  <div className="summary-row" style={{ borderTop: "1px dashed #e5e7eb", paddingTop: "8px", marginTop: "4px" }}>
                    <span className="row-label">👥 Total Guests</span>
                    <b>{people.adults} Adults, {people.kids} Kids</b>
                  </div>
                  <div className="summary-row">
                    <span className="row-label">💰 Base Price</span>
                    <span>₹{summary.price.toLocaleString()}</span>
                  </div>
                  {extraAdults > 0 && (
                    <div className="summary-row">
                      <span className="row-label">👥 Extra Adults ({extraAdults})</span>
                      <span>₹{adultExtra.toLocaleString()}</span>
                    </div>
                  )}
                  {people.kids > 0 && (
                    <div className="summary-row">
                      <span className="row-label">👶 Kids ({people.kids})</span>
                      <span>₹{kidExtra.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="summary-row" style={{ color: "#312AB4", fontWeight: "700" }}>
                    <span className="row-label">🥽 3D Glasses ({totalGuests})</span>
                    <span>₹{glass3DCost.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="summary-divider"></div>
              
              <div className="summary-info-note" style={{ background: "#fef3c7", padding: "10px 12px", borderRadius: "8px", fontSize: "11px", color: "#92400e", marginBottom: "12px" }}>
                💡 <b>Note:</b> Decoration fee ₹700 will be added in the next step.
              </div>
              
              <div className="summary-total-section">
                <div className="summary-row-total">
                  <span className="total-label">Subtotal (excl. Decor)</span>
                  <div className="total-price">
                    <span className="currency">₹</span>
                    <span className="amount">{subtotal.toLocaleString()}</span>
                  </div>
                </div>
                <p className="summary-small-note">Includes base pricing, extra guest charges, and 3D glasses cost.</p>
              </div>

              <div className="advance-payment-box">
                <div className="advance-content">
                  <div className="advance-icon">💳</div>
                  <div className="advance-details">
                    <span className="advance-label">Advance Booking Fee</span>
                    <span className="advance-amount">₹700</span>
                  </div>
                </div>
                <div className="advance-badge">Required</div>
              </div>
              
              <div className="summary-actions">
                <button className="next-btn" onClick={handleNextStep}>
                  <span>Proceed to Next Step</span>
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── AVAILABLE TIME SLOT POPUP MODAL ── */}
      {showPopup && (
        <div className="custom-modal-backdrop" onClick={() => setShowPopup(false)}>
          <div className="custom-modal-box" onClick={(e) => e.stopPropagation()}>
            
            <div className="modal-header-bar">
              <h3>👥 Select Guests ({tempSlot})</h3>
              <button className="modal-close-btn" onClick={() => setShowPopup(false)}>✕</button>
            </div>

            <div className="modal-body-content">
              
              <div className="modal-admin-box">
                <div className="modal-admin-info">
                  <h4>⚙️ Admin Configuration</h4>
                  <p>Fix maximum allowed people limit in real-time:</p>
                </div>
                <input 
                  type="number" 
                  className="modal-admin-input" 
                  value={maxLimit} 
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setMaxLimit(val);
                  }}
                  min="1"
                  title="Admin field to update max capacity"
                />
              </div>

              <span className="modal-section-title">Select Number of People</span>

              <div className="modal-counter-row">
                <div className="modal-counter-label">
                  Adults
                  <span>Ages 12 or above</span>
                </div>
                <div className="modal-controls">
                  <button type="button" className="modal-ctrl-btn" onClick={() => setTempPeople({ ...tempPeople, adults: Math.max(1, tempPeople.adults - 1) })}>−</button>
                  <span className="modal-ctrl-value">{tempPeople.adults}</span>
                  <button type="button" className="modal-ctrl-btn" onClick={() => {
                    if(tempPeople.adults + tempPeople.kids >= maxLimit) {
                      alert(`Reached maximum capacity limit of ${maxLimit} fixed by Admin.`);
                      return;
                    }
                    setTempPeople({ ...tempPeople, adults: tempPeople.adults + 1 });
                  }}>+</button>
                </div>
              </div>

              <div className="modal-counter-row">
                <div className="modal-counter-label">
                  Kids
                  <span>Ages 3 - 11 years</span>
                </div>
                <div className="modal-controls">
                  <button type="button" className="modal-ctrl-btn" onClick={() => setTempPeople({ ...tempPeople, kids: Math.max(0, tempPeople.kids - 1) })}>−</button>
                  <span className="modal-ctrl-value">{tempPeople.kids}</span>
                  <button type="button" className="modal-ctrl-btn" onClick={() => {
                    if(tempPeople.adults + tempPeople.kids >= maxLimit) {
                      alert(`Reached maximum capacity limit of ${maxLimit} fixed by Admin.`);
                      return;
                    }
                    setTempPeople({ ...tempPeople, kids: tempPeople.kids + 1 });
                  }}>+</button>
                </div>
              </div>

              <div className="modal-glass-cost-display">
                <div className="glass-cost-left">
                  <span>🥽</span>
                  <div className="glass-cost-texts">
                    <b>3D Glasses Pricing</b>
                    <span>₹300 per person auto-calculated</span>
                  </div>
                </div>
                <div className="glass-cost-amount">
                  ₹{((tempPeople.adults + tempPeople.kids) * 300).toLocaleString()}
                </div>
              </div>

              <div style={{ fontSize: "11px", color: "#6b7280", textAlign: "center" }}>
                Total Selected: <b>{tempPeople.adults + tempPeople.kids}</b> / Max Limit Enforced: <b>{maxLimit}</b>
              </div>

              <button type="button" className="modal-submit-btn" onClick={handleConfirmGuests}>
                Confirm &amp; Apply Summary
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── STICKY BOTTOM BAR — tablet & mobile only ── */}
      {isSummaryOpenMobile && summary.slot && (
        <StickyBottomBar
          summary={summary}
          subtotal={subtotal}
          formatSummaryDate={formatSummaryDate}
          bookingDate={data.date}
          onNextStep={handleNextStep}
          onDismiss={() => setIsSummaryOpenMobile(false)}
          totalGuests={totalGuests}
          glass3DCost={glass3DCost.toLocaleString()}
        />
      )}
    </div>
  );
};

export default RoomCards1;